import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { STATUSES, SOURCES } from "@/lib/constants";
import { AlertTriangle, Plus, X, Package } from "lucide-react";

const empty = {
  name: "", phone: "", email: "", phones_extra: [], emails_extra: [],
  company: "", designation: "", source: "Manual", status: "New",
  priority: "Medium", budget: 0, follow_up_date: "", tags: [], assigned_to: "", notes: "",
  product: "", setup_cost: 0, monthly_cost: 0, account_type: "",
};

export default function LeadDialog({ open, onOpenChange, lead, members = [], onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const [form, setForm] = useState(empty);
  const [tagsInput, setTagsInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [duplicates, setDuplicates] = useState([]);

  useEffect(() => {
    if (open) {
      const src = lead || empty;
      setForm({
        ...empty,
        ...src,
        phones_extra: Array.isArray(src.phones_extra) ? src.phones_extra : [],
        emails_extra: Array.isArray(src.emails_extra) ? src.emails_extra : [],
      });
      setTagsInput((src.tags || []).join(", "));
      setDuplicates([]);
    }
  }, [open, lead]);

  // Debounced duplicate check on phone/email
  useEffect(() => {
    if (!open) return;
    const phone = (form.phone || "").trim();
    const email = (form.email || "").trim();
    if (!phone && !email) { setDuplicates([]); return; }
    const t = setTimeout(async () => {
      try {
        const params = {};
        if (phone) params.phone = phone;
        if (email) params.email = email;
        if (lead?.id) params.exclude_id = lead.id;
        const { data } = await api.get("/leads/duplicates", { params });
        setDuplicates(data || []);
      } catch { /* ignore */ }
    }, 500);
    return () => clearTimeout(t);
  }, [form.phone, form.email, open, lead?.id]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updExtra = (field, idx, val) => setForm((f) => {
    const arr = [...(f[field] || [])];
    arr[idx] = val;
    return { ...f, [field]: arr };
  });
  const addExtra = (field) => setForm((f) => ({ ...f, [field]: [...(f[field] || []), ""] }));
  const removeExtra = (field, idx) => setForm((f) => {
    const arr = [...(f[field] || [])];
    arr.splice(idx, 1);
    return { ...f, [field]: arr };
  });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        budget: Number(form.budget) || 0,
        setup_cost: Number(form.setup_cost) || 0,
        monthly_cost: Number(form.monthly_cost) || 0,
        phones_extra: (form.phones_extra || []).map((p) => String(p).trim()).filter(Boolean),
        emails_extra: (form.emails_extra || []).map((e) => String(e).trim()).filter(Boolean),
        tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (lead) {
        await api.patch(`/leads/${lead.id}`, payload);
        toast.success("Lead updated");
      } else {
        await api.post("/leads", payload);
        toast.success("Lead created");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-heavy border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">{lead ? "Edit Lead" : "New Lead"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {duplicates.length > 0 && (
            <div className="glass rounded-xl p-3 flex items-start gap-3" data-testid="duplicate-warning">
              <AlertTriangle className="w-4 h-4 text-[#1D63ED] mt-0.5 flex-shrink-0" />
              <div className="flex-1 text-xs">
                <p className="font-medium text-slate-800">
                  {duplicates.length} possible duplicate{duplicates.length > 1 ? "s" : ""} found
                </p>
                <ul className="mt-1 space-y-0.5 text-slate-600">
                  {duplicates.slice(0, 3).map((d) => (
                    <li key={d.id}>
                      <span className="font-medium">{d.name}</span>
                      {d.phone && <span className="text-slate-500"> · {d.phone}</span>}
                      {d.email && <span className="text-slate-500"> · {d.email}</span>}
                    </li>
                  ))}
                  {duplicates.length > 3 && <li className="text-slate-400">…and {duplicates.length - 3} more</li>}
                </ul>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Name *</Label>
              <Input required value={form.name} onChange={(e) => upd("name", e.target.value)} className="mt-1.5 bg-slate-100/70" data-testid="lead-name-input" />
            </div>
            <div>
              <Label>Company</Label>
              <Input value={form.company} onChange={(e) => upd("company", e.target.value)} className="mt-1.5 bg-slate-100/70" data-testid="lead-company-input" />
            </div>
            <div>
              <Label>Designation</Label>
              <Input value={form.designation} onChange={(e) => upd("designation", e.target.value)} className="mt-1.5 bg-slate-100/70" data-testid="lead-designation-input" placeholder="VP Sales, CTO, Manager..." />
            </div>
            <div>
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => upd("source", v)}>
                <SelectTrigger className="mt-1.5 bg-slate-100/70" data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Multi phone */}
          <div>
            <Label>Phone(s)</Label>
            <div className="mt-1.5 space-y-2">
              <div className="flex gap-2">
                <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} className="bg-slate-100/70 flex-1" placeholder="Primary phone" data-testid="lead-phone-input" />
                <span className="text-[10px] text-slate-500 self-center uppercase tracking-wider">Primary</span>
              </div>
              {(form.phones_extra || []).map((p, i) => (
                <div key={i} className="flex gap-2" data-testid={`lead-phone-extra-${i}`}>
                  <Input value={p} onChange={(e) => updExtra("phones_extra", i, e.target.value)} className="bg-slate-100/70 flex-1" placeholder={`Additional phone #${i + 2}`} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeExtra("phones_extra", i)} className="text-slate-500 hover:text-rose-600" data-testid={`remove-phone-${i}`} aria-label="Remove phone">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <button type="button" onClick={() => addExtra("phones_extra")} className="text-xs text-[#1D63ED] hover:underline inline-flex items-center gap-1" data-testid="add-phone-btn">
                <Plus className="w-3 h-3" /> Add another phone
              </button>
            </div>
          </div>

          {/* Multi email */}
          <div>
            <Label>Email(s)</Label>
            <div className="mt-1.5 space-y-2">
              <div className="flex gap-2">
                <Input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} className="bg-slate-100/70 flex-1" placeholder="Primary email" data-testid="lead-email-input" />
                <span className="text-[10px] text-slate-500 self-center uppercase tracking-wider">Primary</span>
              </div>
              {(form.emails_extra || []).map((em, i) => (
                <div key={i} className="flex gap-2" data-testid={`lead-email-extra-${i}`}>
                  <Input type="email" value={em} onChange={(e) => updExtra("emails_extra", i, e.target.value)} className="bg-slate-100/70 flex-1" placeholder={`Additional email #${i + 2}`} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeExtra("emails_extra", i)} className="text-slate-500 hover:text-rose-600" data-testid={`remove-email-${i}`} aria-label="Remove email">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <button type="button" onClick={() => addExtra("emails_extra")} className="text-xs text-[#1D63ED] hover:underline inline-flex items-center gap-1" data-testid="add-email-btn">
                <Plus className="w-3 h-3" /> Add another email
              </button>
            </div>
          </div>

          {/* Product & pricing */}
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-4 h-4 text-indigo-600" />
              <p className="text-xs uppercase tracking-widest text-slate-600 font-medium">Product & pricing</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Product</Label>
                <Input value={form.product} onChange={(e) => upd("product", e.target.value)} placeholder="e.g. LeadFlow Pro, Custom CRM" className="mt-1.5 bg-white" data-testid="lead-product-input" />
              </div>
              <div>
                <Label>Account type</Label>
                <Select value={form.account_type || "none"} onValueChange={(v) => upd("account_type", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5 bg-white" data-testid="lead-account-type-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    <SelectItem value="prepaid">Prepaid</SelectItem>
                    <SelectItem value="postpaid">Postpaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>One-time setup cost (₹)</Label>
                <Input type="number" min="0" value={form.setup_cost} onChange={(e) => upd("setup_cost", e.target.value)} className="mt-1.5 bg-white" data-testid="lead-setup-cost-input" />
              </div>
              <div>
                <Label>Monthly recurring cost (₹)</Label>
                <Input type="number" min="0" value={form.monthly_cost} onChange={(e) => upd("monthly_cost", e.target.value)} className="mt-1.5 bg-white" data-testid="lead-monthly-cost-input" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => upd("status", v)}>
                <SelectTrigger className="mt-1.5 bg-slate-100/70" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => upd("priority", v)}>
                <SelectTrigger className="mt-1.5 bg-slate-100/70" data-testid="lead-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Budget (₹)</Label>
              <Input type="number" min="0" value={form.budget} onChange={(e) => upd("budget", e.target.value)} className="mt-1.5 bg-slate-100/70" data-testid="lead-budget-input" />
            </div>
            <div>
              <Label>Follow-up date</Label>
              <Input type="date" value={form.follow_up_date} onChange={(e) => upd("follow_up_date", e.target.value)} className="mt-1.5 bg-slate-100/70" data-testid="lead-followup-input" />
            </div>
            {isAdmin && (
              <div className="md:col-span-2">
                <Label>Assign to</Label>
                <Select value={form.assigned_to || "unassigned"} onValueChange={(v) => upd("assigned_to", v === "unassigned" ? "" : v)}>
                  <SelectTrigger className="mt-1.5 bg-slate-100/70" data-testid="lead-assign-select"><SelectValue placeholder="Choose member" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {members.map((m) => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div>
            <Label>Tags (comma separated)</Label>
            <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="hot, decision-maker, follow-up-soon" className="mt-1.5 bg-slate-100/70" data-testid="lead-tags-input" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="mt-1.5 bg-slate-100/70" data-testid="lead-notes-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#1D63ED] hover:bg-[#1854D9] text-white" data-testid="lead-save-btn">
              {saving ? "Saving…" : (lead ? "Save changes" : "Create Lead")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
